import type { ReactNode } from "react";
import { TopoTexture } from "@/components/TopoTexture";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";

interface EntryShellProps {
  children: ReactNode;
  /** Top-left; defaults to the Zettl logo linking home. */
  start?: ReactNode;
  /** Top-right: language and theme switches. */
  end?: ReactNode;
  className?: string;
}

/**
 * The frame of every page before the app: sign-in, password reset, invite and
 * join links. Same background, contour band and card as the app itself, so
 * the first screen already looks like what comes after it.
 */
export function EntryShell({ children, start, end, className }: EntryShellProps) {
  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-x-hidden bg-background text-foreground">
      {/* The app's contour band, taller here: it is the page's only ornament. */}
      <TopoTexture
        className="inset-x-0 top-0 -z-10 h-[70vh] text-[hsl(var(--action))] opacity-[0.26] dark:text-primary dark:opacity-[0.14]"
        size="1600px"
        position="center top"
      />
      <header className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6">
        {start ?? (
          // Plain Link: "/" redirects to the visitor's language, and the shell
          // must render outside the LanguageProvider too (password reset).
          <Link to="/" aria-label="Zettl">
            <Logo size="sm" />
          </Link>
        )}
        {end && <div className="flex items-center gap-2">{end}</div>}
      </header>
      <main
        className={cn(
          "flex flex-1 items-start justify-center px-4 pb-16 pt-[8vh] sm:items-center sm:pt-0",
          className
        )}
      >
        {children}
      </main>
    </div>
  );
}

/** The card the entry pages sit in: the app's Card, lifted off the texture. */
export const entryCardClass = "w-full max-w-md shadow-xl shadow-black/5 dark:shadow-black/40";
